// RiskHeatmapDots — overlay for rendering multiple risks on a grid.
// Sub-divides each cell into an odd-dimensioned inner grid (1×1, 3×3, 5×5, …).
// First risk goes in the center; subsequent risks spiral outward.
// All dots are the same size. Numbered dots show index matching the table legend.
// Custom instant tooltip on hover (no browser delay).

import React, { useState, useCallback } from 'react'
import type { PlottedRisk } from './types'
import { cellOrigin, cellWidth, cellHeight, VIEWBOX_W } from './risk-grid-utils'

interface RiskHeatmapDotsProps {
  risks: PlottedRisk[]
  cols: number
  rows: number
  /** Fixed dot radius in viewBox units (default: 6) */
  dotRadius?: number
  /** Map of risk ID → display number (from the table legend). Unnumbered risks get plain dots. */
  riskNumbers?: Map<string, number>
  onRiskClick?: (riskId: string) => void
}

/**
 * Returns an array of (dx, dy) offsets relative to the center of an odd-sized grid.
 * Index 0 is (0,0) = center, then spirals outward clockwise.
 */
function spiralOffsets(capacity: number): { dx: number; dy: number }[] {
  const offsets: { dx: number; dy: number }[] = [{ dx: 0, dy: 0 }]
  let x = 0
  let y = 0
  let step = 1

  while (offsets.length < capacity) {
    for (let i = 0; i < step && offsets.length < capacity; i++) {
      x++
      offsets.push({ dx: x, dy: y })
    }
    for (let i = 0; i < step && offsets.length < capacity; i++) {
      y++
      offsets.push({ dx: x, dy: y })
    }
    step++
    for (let i = 0; i < step && offsets.length < capacity; i++) {
      x--
      offsets.push({ dx: x, dy: y })
    }
    for (let i = 0; i < step && offsets.length < capacity; i++) {
      y--
      offsets.push({ dx: x, dy: y })
    }
    step++
  }

  return offsets
}

/** Returns the smallest odd number n such that n×n >= count. */
function innerGridSize(count: number): number {
  const side = Math.ceil(Math.sqrt(count))
  return side % 2 === 0 ? side + 1 : side
}

interface TooltipState {
  name: string
  x: number
  y: number
}

export function RiskHeatmapDots({
  risks,
  cols,
  rows,
  dotRadius = 8,
  riskNumbers,
  onRiskClick,
}: RiskHeatmapDotsProps) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)

  const showTooltip = useCallback((name: string, x: number, y: number) => {
    setTooltip({ name, x, y })
  }, [])

  const hideTooltip = useCallback(() => {
    setTooltip(null)
  }, [])

  // Group risks by cell
  const cellGroups = new Map<string, PlottedRisk[]>()
  for (const risk of risks) {
    const key = `${risk.likelihood}-${risk.impact}`
    const group = cellGroups.get(key) ?? []
    group.push(risk)
    cellGroups.set(key, group)
  }

  const cw = cellWidth(cols)
  const ch = cellHeight(rows)
  const inset = 4

  const dots: React.ReactElement[] = []

  for (const [, group] of cellGroups) {
    const first = group[0]!
    const origin = cellOrigin(first.likelihood, first.impact, cols, rows)

    const count = group.length
    const gridN = innerGridSize(count)
    const offsets = spiralOffsets(count)

    const areaW = cw - inset * 2
    const areaH = ch - inset * 2
    const subW = areaW / gridN
    const subH = areaH / gridN

    const centerX = origin.x + cw / 2
    const centerY = origin.y + ch / 2

    for (let i = 0; i < count; i++) {
      const risk = group[i]!
      const offset = offsets[i]!

      const cx = centerX + offset.dx * subW
      const cy = centerY + offset.dy * subH

      const num = riskNumbers?.get(risk.id)
      const hasNumber = num !== undefined

      dots.push(
        <g
          key={risk.id}
          className={onRiskClick ? 'cursor-pointer' : undefined}
          onClick={() => onRiskClick?.(risk.id)}
          onMouseEnter={() => showTooltip(risk.name, cx, cy)}
          onMouseLeave={hideTooltip}
          role="button"
          aria-label={risk.name}
        >
          <circle
            cx={cx}
            cy={cy}
            r={dotRadius}
            fill={
              risk.outsideAppetite
                ? 'hsl(var(--grid-node-inherent))'
                : 'hsl(var(--grid-node-residual))'
            }
            stroke="hsl(var(--background))"
            strokeWidth={1}
          />
          {hasNumber && (
            <text
              x={cx}
              y={cy}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={8}
              fontWeight="bold"
              fill="white"
              pointerEvents="none"
            >
              {num}
            </text>
          )}
        </g>,
      )
    }
  }

  // Position tooltip above the dot, clamped within the viewBox
  const tooltipW = 180
  const tooltipH = 28
  let tipX = tooltip ? tooltip.x - tooltipW / 2 : 0
  let tipY = tooltip ? tooltip.y - dotRadius - tooltipH - 4 : 0
  if (tipX < 2) tipX = 2
  if (tipX + tooltipW > VIEWBOX_W - 2) tipX = VIEWBOX_W - tooltipW - 2
  if (tooltip && tipY < 2) {
    tipY = tooltip.y + dotRadius + 4
  }

  return (
    <g>
      {dots}
      {tooltip && (
        <foreignObject x={tipX} y={tipY} width={tooltipW} height={tooltipH}>
          <div
            style={{
              background: 'hsl(var(--popover))',
              color: 'hsl(var(--popover-foreground))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '6px',
              padding: '4px 8px',
              fontSize: '11px',
              lineHeight: '1.3',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              pointerEvents: 'none',
              boxShadow: '0 2px 4px rgba(0,0,0,0.12)',
            }}
          >
            {tooltip.name}
          </div>
        </foreignObject>
      )}
    </g>
  )
}
